import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import { OperaContext } from "../../context/OperaContext";
import ImageModal from "../../components/ImagePreviewModal";
import useGoogleDriveImagePicker from "../../components/useGoogleDriveImagePicker";
import ImagePreview from "../../components/imagePreview";
import Collapsible from "../../components/Collapsible";
import { toast } from "react-toastify";
import RichTextEditor from "../../components/RichTextEditor";

function HotelTab({ setActiveTab }) {
  const { venues = [], fetchAdminData } = useContext(OperaContext);
  const [localHotels, setLocalHotels] = useState([]);
  const initialHotelState = {
    name: "",
    source: "manual",
    address: "",
    city: "",
    cityNew: "",
    associatedVenues: [],
    markupPrice: 0,
    description: "",
    starRating: 0,
    amenities: ["WiFi"],
    images: [],
    roomTypes: [
      {
        type: "",
        pricePerNight: 0,
        capacity: 0,
        description: "",
        amenities: [],
        images: [],
      },
    ],
    published: true,
  };
  const [showPreview, setShowPreview] = useState(false);
  const [newHotel, setNewHotel] = useState({ ...initialHotelState });
  const [editingHotel, setEditingHotel] = useState(null);
  const [hiddenHotels, setHiddenHotels] = useState([]);
  const [showHiddenHotels, setShowHiddenHotels] = useState(false);
  const [error, setError] = useState(null);
  // const [syncStatus, setSyncStatus] = useState(null);
  const hotelFormRef = useRef(null);
  const [cityFilter, setCityFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const [newCities, setCities] = useState([]);

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };
  const availableAmenities = [
    "Pool",
    "Spa",
    "Restaurant",
    "Parking",
    "Nearby Parking",
    "Bar",
    "Breakfast",
    "Luggage Service",
  ];

  const normalizeAmenity = (amenity) => {
    if (amenity === "WiFi") return "WiFi";
    return amenity
      .trim()
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleHotelChange = (e) => {
    const { name, value } = e.target;
    if (name === "markupPrice" || name === "starRating") {
      setNewHotel((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
      if (editingHotel)
        setEditingHotel((prev) => ({
          ...prev,
          [name]: parseFloat(value) || 0,
        }));
    } else if (name === "amenities") {
      const selectedAmenities = Array.from(e.target.selectedOptions, (option) =>
        normalizeAmenity(option.value)
      );
      const amenitiesWithWiFi = [...new Set(["WiFi", ...selectedAmenities])];
      const invalidAmenities = selectedAmenities.filter(
        (amenity) => !availableAmenities.includes(amenity)
      );
      if (invalidAmenities.length > 0) {
        console.warn(
          "Selected amenities not in available list:",
          invalidAmenities
        );
      }
      setNewHotel((prev) => ({ ...prev, amenities: amenitiesWithWiFi }));
      if (editingHotel)
        setEditingHotel((prev) => ({ ...prev, amenities: amenitiesWithWiFi }));
    } else if (name === "cityNew") {
      // Find the city name when cityNew is selected
      const selectedCity = newCities.find((city) => city._id === value);
      const cityName = selectedCity ? selectedCity.name : "";

      setNewHotel((prev) => ({
        ...prev,
        [name]: value,
        city: cityName, // Also set the city name
      }));
      if (editingHotel)
        setEditingHotel((prev) => ({
          ...prev,
          [name]: value,
          city: cityName, // Also set the city name
        }));
    } else {
      setNewHotel((prev) => ({ ...prev, [name]: value }));
      if (editingHotel) setEditingHotel((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDescriptionChange = (field, value) => {
    setEditingHotel((prev) => ({ ...prev, [field]: value }));
    if (editingHotel) {
      setEditingHotel((prev) => ({ ...prev, [field]: value }));
    }
  };

  const fetchCities = useCallback(async () => {
    try {
      const response = await axios.get("/api/cities", { timeout: 15000 });
      const fetchedCities = response.data;
      setCities(fetchedCities);
    } catch (err) {
      setError("Failed to load cities. Please try again later.");
    }
  }, []);
  const handleHotelImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (editingHotel) setEditingHotel((prev) => ({ ...prev, images: files }));
    if (!editingHotel) setNewHotel((prev) => ({ ...prev, images: files }));
  };

  const handleAssociatedVenuesChange = (e) => {
    const options = e.target.options;
    const selectedVenues = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedVenues.push(options[i].value);
      }
    }
    setNewHotel((prev) => ({ ...prev, associatedVenues: selectedVenues }));
    if (editingHotel)
      setEditingHotel((prev) => ({
        ...prev,
        associatedVenues: selectedVenues,
      }));
  };

  const handleRoomTypeChange = (index, field, value) => {
    const updatedRoomTypes = [
      ...(editingHotel ? editingHotel.roomTypes : newHotel.roomTypes),
    ];
    if (field === "amenities") {
      updatedRoomTypes[index] = {
        ...updatedRoomTypes[index],
        [field]: value
          .split(",")
          .map((item) => normalizeAmenity(item))
          .filter((item) => item),
      };
    } else if (field === "pricePerNight" || field === "capacity") {
      updatedRoomTypes[index] = {
        ...updatedRoomTypes[index],
        [field]: parseFloat(value) || 0,
      };
    } else {
      updatedRoomTypes[index] = {
        ...updatedRoomTypes[index],
        [field]: value,
      };
    }
    setNewHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
    if (editingHotel)
      setEditingHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
  };

  const addRoomType = () => {
    const updatedRoomTypes = [
      ...(editingHotel ? editingHotel.roomTypes : newHotel.roomTypes),
      {
        type: "",
        pricePerNight: 0,
        capacity: 0,
        description: "",
        amenities: [],
        images: [],
      },
    ];
    setNewHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
    if (editingHotel)
      setEditingHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
  };

  const removeRoomType = (index) => {
    const updatedRoomTypes = [
      ...(editingHotel ? editingHotel.roomTypes : newHotel.roomTypes),
    ];
    updatedRoomTypes.splice(index, 1);
    setNewHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
    if (editingHotel)
      setEditingHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
  };

  const handleRoomTypeImageChange = (index, e) => {
    const files = Array.from(e.target.files);
    const updatedRoomTypes = [
      ...(editingHotel ? editingHotel.roomTypes : newHotel.roomTypes),
    ];
    updatedRoomTypes[index] = { ...updatedRoomTypes[index], images: files };

    if (editingHotel)
      setEditingHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
    if (!editingHotel)
      setNewHotel((prev) => ({ ...prev, roomTypes: updatedRoomTypes }));
  };

  const fetchHotelsDirectly = async () => {
    try {
      const response = await axios.get("/api/hotels", {
        withCredentials: true,
        params: {
          limit: 1000,
          published: undefined,
        },
      });

      const hotels = response.data.hotels || [];
      setLocalHotels(hotels);
      return hotels;
    } catch (error) {
      console.error("Error fetching hotels directly:", error);
      setError(
        `Failed to fetch hotels: ${error.response?.data?.message || error.message
        }`
      );
      return [];
    }
  };

  const fetchHiddenHotels = async () => {
    try {
      const res = await axios.get("/api/hotels", {
        params: { published: false },
      });
      setHiddenHotels(res.data.hotels);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch hidden hotels");
    }
  };

  const validateForm = () => {
    if (!newHotel.name) return "Hotel name is required";
    if (!newHotel.address) return "Address is required";
    if (!newHotel.cityNew) return "City is required";
    if (newHotel.roomTypes.length === 0)
      return "At least one room type is required";
    for (const [index, roomType] of newHotel.roomTypes.entries()) {
      if (!roomType.type)
        return `Room type at position ${index + 1} must have a type`;
      if (typeof roomType.pricePerNight !== "number")
        return `Room type at position ${index + 1
          } must have a valid price per night`;
      if (typeof roomType.capacity !== "number" || roomType.capacity < 1)
        return `Room type at position ${index + 1
          } must have a valid capacity (minimum 1)`;
    }
    return null;
  };

  const resetForm = () => {
    setShowPreview(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setNewHotel({ ...initialHotelState });
    setEditingHotel(null);
    if (hotelFormRef.current) {
      hotelFormRef.current.reset();
    }
  };

  const handleHotelSubmit = async (e, retryCount = 0, maxRetries = 3) => {
    e.preventDefault();
    setError(null);
    setShowPreview(false);
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const formData = new FormData();
    formData.append("name", newHotel.name);
    formData.append("source", newHotel.source);
    formData.append("address", newHotel.address);
    formData.append("city", newHotel.city || ""); // Add the missing city field
    formData.append("cityNew", newHotel.cityNew);
    formData.append(
      "associatedVenues",
      JSON.stringify(newHotel.associatedVenues)
    );
    formData.append("markupPrice", newHotel.markupPrice);
    formData.append("description", newHotel.description || "");
    formData.append("starRating", newHotel.starRating);
    formData.append("amenities", JSON.stringify(newHotel.amenities));
    formData.append("published", newHotel.published);

    if (newHotel.images && newHotel.images.length > 0) {
      newHotel.images.forEach((image) => {
        formData.append("images", image);
      });
    } else if (editingHotel && editingHotel.images) {
      editingHotel.images.forEach((image) => {
        formData.append("images", image);
      });
    }

    newHotel.roomTypes.forEach((roomType) => {
      if (roomType.images && roomType.images.length > 0) {
        roomType.images.forEach((image) => {
          formData.append("roomTypesImages", image);
        });
      }
    });
    if (editingHotel) {
      editingHotel?.roomTypes.forEach((roomType) => {
        if (roomType?.images && roomType?.images.length > 0) {
          roomType?.images.forEach((image) => {
            formData.append("roomTypesImages", image);
          });
        }
      });
    }

    const roomTypesForSubmission = newHotel.roomTypes.map((rt) => ({
      type: rt.type,
      pricePerNight: rt.pricePerNight,
      capacity: rt.capacity,
      description: rt.description !== undefined ? rt.description : "",
      amenities: rt.amenities || [],
      imageCount: rt.images ? rt.images.length : 0,
    }));
    formData.append("roomTypes", JSON.stringify(roomTypesForSubmission));

    try {
      const url = editingHotel
        ? `/api/hotels/${editingHotel._id}`
        : "/api/hotels";
      const method = editingHotel ? "put" : "post";

      const result = await axios({
        method,
        url,
        data: formData,
        timeout: 15000,
      });

      if (result.status === 200 || result.status === 201) {
        toast.success(
          `Hotel ${editingHotel ? "updated" : "created"} successfully!`
        );

        // Wait a moment for the database to update
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Refresh hotel data
        await fetchHotelsDirectly();

        // Update context in background
        fetchAdminData().catch((err) =>
          console.warn("Context update failed:", err)
        );

        if (showHiddenHotels) fetchHiddenHotels();
        setIsSectionOpen(false);
        resetForm();
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (err) {
      console.error("Error in hotel submit:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to save hotel. Please check server logs.";
      toast.error(errorMessage);
      setError(errorMessage);
    }
  };

  const editHotel = async (hotel) => {
    setIsSectionOpen(true);
    try {
      const res = await axios.get(`/api/hotels/${hotel._id}`);
      const hotelData = res.data;
      const normalizedAmenities = hotelData.amenities.map(normalizeAmenity);
      const amenitiesWithWiFi = normalizedAmenities.includes("WiFi")
        ? normalizedAmenities
        : ["WiFi", ...normalizedAmenities];
      const invalidAmenities = amenitiesWithWiFi.filter(
        (amenity) => !availableAmenities.includes(amenity) && amenity !== "WiFi"
      );
      if (invalidAmenities.length > 0) {
        console.warn(
          `Hotel "${hotelData.name}" has amenities not in available list: `,
          invalidAmenities
        );
      }
      setEditingHotel({
        ...hotelData,
        amenities: amenitiesWithWiFi,
      });

      setNewHotel({
        name: hotelData.name,
        source: hotelData.source,
        address: hotelData.address,
        city: hotelData?.cityNew?.name || hotelData.city || "",
        cityNew: hotelData?.cityNew?._id,
        associatedVenues: hotelData.associatedVenues.map((v) => v._id || v),
        markupPrice: hotelData.markupPrice,
        description: hotelData.description || "",
        starRating: hotelData.starRating,
        amenities: amenitiesWithWiFi,
        images: [],
        roomTypes:
          hotelData.roomTypes.length > 0
            ? hotelData.roomTypes.map((rt) => ({
              type: rt.type,
              pricePerNight: rt.pricePerNight,
              capacity: rt.capacity,
              description: rt.description || "",
              amenities: rt.amenities.map(normalizeAmenity) || [],
              images: rt.images || [], // Preserve existing images
            }))
            : [
              {
                type: "",
                pricePerNight: 0,
                capacity: 0,
                description: "",
                amenities: [],
                images: [],
              },
            ],
        published: hotelData.published,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch hotel details");
    }
  };

  const deleteHotel = async (hotelId, isHidden = false) => {
    if (!window.confirm("Are you sure you want to delete this hotel?")) return;
    try {
      await axios.delete(`/api/hotels/${hotelId}`);
      await fetchHotelsDirectly();
      fetchAdminData().catch((err) =>
        console.warn("Context update after delete failed:", err)
      );
      if (showHiddenHotels) fetchHiddenHotels();
      resetForm();
      setError(null);
      toast.success("Hotel deleted successfully!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to delete hotel";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const togglePublishHotel = async (hotelId, published) => {
    try {
      await axios.put(`/api/hotels/${hotelId}/publish`, { published });
      await fetchHotelsDirectly();
      fetchAdminData().catch((err) =>
        console.warn("Context update after toggle failed:", err)
      );
      if (showHiddenHotels) fetchHiddenHotels();
      setError(null);
      toast.success(
        `Hotel ${published ? "published" : "hidden"} successfully!`
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to toggle hotel visibility";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Sync hotels functionality - uncomment when sync UI is implemented
  // const syncHotels = async () => {
  //   try {
  //     const venueId = newHotel.associatedVenues[0] || venues[0]?._id;
  //     if (!venueId) {
  //       setError("Please select at least one venue to sync hotels");
  //       return;
  //     }
  //     const res = await axios.post("/api/hotels/sync-api", { venueId });
  //     setSyncStatus(res.data);
  //     fetchData();
  //     if (showHiddenHotels) fetchHiddenHotels();
  //     setError(null);
  //   } catch (err) {
  //     setError(err.response?.data?.message || "Failed to sync hotels");
  //   }
  // };

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    if (showHiddenHotels) fetchHiddenHotels();
    else setHiddenHotels([]);
  }, [showHiddenHotels]);

  // Ensure admin data is loaded when component mounts
  useEffect(() => {
    const loadHotels = async () => {
      try {
        await fetchHotelsDirectly();
        await fetchAdminData();
      } catch (err) {
        console.error("Error fetching admin data on mount:", err);
        setError("Failed to load hotels data");
      }
    };
    loadHotels();
  }, [fetchAdminData]);

  const filterHotelsByCity = (hotelsList) => {
    if (!cityFilter) return hotelsList;
    return hotelsList.filter((hotel) =>
      hotel?.cityNew?.name.toLowerCase().includes(cityFilter?.toLowerCase())
    );
  };

  // Use localHotels for admin panel
  const displayHotels = localHotels;

  const filteredPublishedHotels = filterHotelsByCity(
    displayHotels.filter((hotel) => hotel && hotel.published === true)
  );

  const filteredHiddenHotels = filterHotelsByCity(hiddenHotels);

  const filteredVenues = newHotel.cityNew
    ? venues.filter((venue) => venue?.cityNew?._id === newHotel.cityNew)
    : venues;

  const cancelEdit = () => {
    resetForm();
    setIsSectionOpen(false);
  };
  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing: editingHotel ? true : false,
    setFormData: setNewHotel,
    setFormDataEdit: setEditingHotel,
    setError,
    isHotel: true,
  });
  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white">
      <ImageModal
        open={modalOpen}
        image={modalImage}
        onClose={() => setModalOpen(false)}
      />
      <Collapsible
        title={editingHotel ? "Edit Hotel" : "Add New Hotel"}
        defaultOpen={true}
        isOpen={isSectionOpen}
        setIsOpen={setIsSectionOpen}
      >
        <div>
          {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}
          <form
            onSubmit={handleHotelSubmit}
            ref={hotelFormRef}
            className="flex flex-col gap-4 mb-8"
          >
            <div className="flex flex-col gap-1">
              <label className="text-white">Hotel Name *</label>
              <input
                type="text"
                name="name"
                value={newHotel.name}
                onChange={handleHotelChange}
                placeholder="e.g., Hotel Sacher"
                required
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Address *</label>
              <input
                type="text"
                name="address"
                value={newHotel.address}
                onChange={handleHotelChange}
                placeholder="e.g., 123 Main St, Vienna, Austria"
                required
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
              />
            </div>
            <div className="flex flex-col gap-1 mb-4">
              <label className="text-white">City *</label>
              <select
                name="cityNew"
                value={newHotel.cityNew}
                onChange={handleHotelChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md focus:border-[#d4a017]"
              >
                <option value="">Select City</option>
                {newCities?.map((cities) => (
                  <option key={cities._id} value={cities._id}>
                    {cities.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Associated Venues *</label>
              <select
                multiple
                name="associatedVenues"
                value={newHotel.associatedVenues}
                onChange={handleAssociatedVenuesChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md h-32"
              >
                {filteredVenues.map((venue) => (
                  <option key={venue._id} value={venue._id}>
                    {venue.name} - {venue?.cityNew?.name}
                  </option>
                ))}
              </select>
              <p className="text-[#ccc] text-sm">
                Hold Ctrl (Windows) or Cmd (Mac) to select multiple venues in
                the same city.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Additional Price (€)</label>
              <input
                type="number"
                name="markupPrice"
                value={newHotel.markupPrice}
                onChange={handleHotelChange}
                placeholder="e.g., 50 for €50 per person for 2 nights"
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
              />
              <p className="text-[#ccc] text-sm">
                Additional cost when selecting this hotel instead of the default
                hotel (0 for default hotels).
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Description (Optional)</label>
              <button
                type="button"
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
                onClick={() => setShowPreview((prev) => !prev)}
              >
                {showPreview ? "Edit Text" : "Edit HTML"}
              </button>
              {showPreview && <textarea
                name="description"
                value={newHotel.description}
                onChange={handleHotelChange}
                placeholder="e.g., Luxury hotel near the opera house"
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[150px] resize-y"
              />}
              {!showPreview && <RichTextEditor
                value={newHotel.description}
                height={200}
                onChange={(content) =>
                  handleDescriptionChange("description", content)
                }
              />}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Star Rating (Optional, 1-5)</label>
              <input
                type="number"
                name="starRating"
                value={newHotel.starRating}
                onChange={handleHotelChange}
                placeholder="e.g., 5"
                min="1"
                max="5"
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">
                Additional Amenities (WiFi is included by default)
              </label>
              <select
                multiple
                name="amenities"
                value={newHotel.amenities.filter(
                  (amenity) => amenity !== "WiFi"
                )}
                onChange={handleHotelChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md h-32"
              >
                {availableAmenities.map((amenity, index) => (
                  <option key={index} value={amenity}>
                    {amenity}
                  </option>
                ))}
              </select>
              <p className="text-[#ccc] text-sm">
                Hold Ctrl (Windows) or Cmd (Mac) to select multiple amenities.
                WiFi is included by default.
              </p>
            </div>
            {editingHotel?.images &&
              editingHotel.images.map((image, imgIndex) => (
                <div key={imgIndex}>
                  <ImagePreview image={image} onClick={openModal} />
                </div>
              ))}
            {newHotel?.images &&
              newHotel.images.map((image, imgIndex) => (
                <div key={imgIndex}>
                  <ImagePreview image={image} onClick={openModal} />
                </div>
              ))}
            <div className="flex flex-col gap-1">
              <label className="text-white">Hotel Images (Optional)</label>
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    type="file"
                    id="hotel-images-file"
                    multiple
                    name="images"
                    onChange={handleHotelImageChange}
                    accept="image/*"
                    className="w-fit pl-10 pr-3 py-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
                  />
                  <label
                    htmlFor="hotel-images-file"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
                  >
                    <img
                      src="/images/attach.svg"
                      alt="Choose File"
                      className="w-4 h-4 filter invert"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenPicker("images")}
                  className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
                >
                  <img
                    src="/images/drive.png"
                    alt="Google Drive"
                    className="w-4 h-4"
                  />
                  Google Drive
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-white">
                Room Types (At least one required)
              </label>
              {(editingHotel ? editingHotel.roomTypes : newHotel.roomTypes).map(
                (roomType, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-2 border border-[#555] p-4 rounded-md"
                  >
                    <div className="flex flex-col gap-1">
                      <label className="text-white">Room Type *</label>
                      <input
                        type="text"
                        placeholder="e.g., Standard"
                        value={roomType.type}
                        onChange={(e) =>
                          handleRoomTypeChange(index, "type", e.target.value)
                        }
                        className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-white">
                        Additional Cost for Room (€) *
                      </label>
                      <input
                        type="number"
                        placeholder="e.g., 50"
                        value={roomType.pricePerNight}
                        onChange={(e) =>
                          handleRoomTypeChange(
                            index,
                            "pricePerNight",
                            e.target.value
                          )
                        }
                        className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
                      />
                      <p className="text-[#ccc] text-sm">
                        Upgrade cost when selecting this room type instead of
                        the default room (0 for default room types).
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-white">Capacity (Guests) *</label>
                      <input
                        type="number"
                        placeholder="e.g., 2"
                        value={roomType.capacity}
                        onChange={(e) =>
                          handleRoomTypeChange(
                            index,
                            "capacity",
                            e.target.value
                          )
                        }
                        className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-white">
                        Description (Optional)
                      </label>
                      <textarea
                        placeholder="e.g., Spacious room with a double bed"
                        value={roomType.description}
                        onChange={(e) =>
                          handleRoomTypeChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa] min-h-[100px] resize-y"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-white">Amenities (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., King Bed, WiFi"
                        value={roomType.amenities.join(", ")}
                        onChange={(e) =>
                          handleRoomTypeChange(
                            index,
                            "amenities",
                            e.target.value
                          )
                        }
                        className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
                      />
                      <p className="text-[#ccc] text-sm">
                        Enter room-specific amenities as a comma-separated list.
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-white">
                        Room Images (Optional)
                      </label>
                      {roomType?.images &&
                        roomType.images.map((image, imgIndex) => (
                          <div key={imgIndex}>
                            <ImagePreview image={image} onClick={openModal} />
                          </div>
                        ))}
                      <div className="flex gap-2">
                        <div className="relative">
                          <input
                            type="file"
                            id={`roomtype - ${index} - images - file`}
                            multiple
                            onChange={(e) =>
                              handleRoomTypeImageChange(index, e)
                            }
                            accept="image/*"
                            className="w-fit pl-10 pr-3 py-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
                          />
                          <label
                            htmlFor={`roomtype - ${index} - images - file`}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
                          >
                            <img
                              src="/images/attach.svg"
                              alt="Choose File"
                              className="w-4 h-4 filter invert"
                            />
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpenPicker("images")}
                          className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
                        >
                          <img
                            src="/images/drive.png"
                            alt="Google Drive"
                            className="w-4 h-4"
                          />
                          Google Drive
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRoomType(index)}
                      className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors mt-2"
                    >
                      Remove Room Type
                    </button>
                  </div>
                )
              )}
              <button
                type="button"
                onClick={addRoomType}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors"
              >
                Add Room Type
              </button>
            </div>
            <button
              type="submit"
              className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors"
            >
              {editingHotel ? "Update Hotel" : "Add Hotel"}
            </button>
            {editingHotel && (
              <div className="flex flex-col md:flex-row gap-2">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="w-full md:w-auto px-4 py-2 bg-[#333] hover:bg-[#555] text-white rounded-md transition-colors"
                >
                  Cancel Edit
                </button>
                <button
                  type="button"
                  onClick={() =>
                    deleteHotel(editingHotel._id, !editingHotel.published)
                  }
                  className="w-full md:w-auto px-4 py-2 bg-[#f44336] hover:bg-[#da190b] text-white rounded-md transition-colors"
                >
                  Delete Hotel
                </button>
              </div>
            )}
          </form>
        </div>
      </Collapsible>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left">
          Existing Hotels
        </h3>
        <div className="flex justify-center md:justify-start mt-4">
          <label className="flex items-center gap-2 text-white">
            <input
              type="checkbox"
              checked={showHiddenHotels}
              onChange={(e) => setShowHiddenHotels(e.target.checked)}
              className="h-4 w-4 text-[#d4a017] bg-[#333] border-[#555] rounded"
            />
            Show Hidden Hotels
          </label>
        </div>
      </div>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Filter by city..."
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
        />
      </div>

      {filteredPublishedHotels.length === 0 ? (
        <p className="text-[#ccc] text-center">
          No visible hotels available
          {cityFilter ? ` matching "${cityFilter}"` : ""}.
        </p>
      ) : (
        <div className="p-2">
          <ul className="list-none p-0">
            {filteredPublishedHotels.map((hotel) => (
              <li
                key={hotel._id}
                className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444] last:border-b-0"
              >
                <div className="flex-1 text-center md:text-left">
                  <span className="text-white">
                    {hotel.name} - {hotel?.cityNew?.name}
                  </span>
                  <p className="text-white text-sm">
                    Room Types:{" "}
                    {hotel.roomTypes
                      .map((rt) => `${rt.type}(€${rt.pricePerNight} / night)`)
                      .join(", ")}
                  </p>
                  <p className="text-white text-sm">
                    Venues:{" "}
                    {hotel.associatedVenues.map((v) => v.name).join(", ") ||
                      "None"}
                  </p>
                </div>
                <div className="flex flex-row gap-2 mt-2 md:mt-0">
                  <button
                    onClick={() => editHotel(hotel)}
                    className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded-md transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => togglePublishHotel(hotel._id, false)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                  >
                    Hide
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showHiddenHotels && (
        <>
          <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mt-4 mb-4">
            Hidden Hotels
          </h3>
          {filteredHiddenHotels.length === 0 ? (
            <p className="text-[#ccc] text-center">
              No hidden hotels found
              {cityFilter ? ` matching "${cityFilter}"` : ""}.
            </p>
          ) : (
            <div className="p-2">
              <ul className="list-none p-0">
                {filteredHiddenHotels.map((hotel) => (
                  <li
                    key={hotel._id}
                    className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444] last:border-b-0"
                  >
                    <div className="flex-1 text-center md:text-left">
                      <span className="text-white">
                        {hotel.name} - {hotel.city}
                      </span>
                      <p className="text-white text-sm">
                        Room Types:{" "}
                        {hotel.roomTypes
                          .map(
                            (rt) => `${rt.type}(€${rt.pricePerNight} / night)`
                          )
                          .join(", ")}
                      </p>
                      <p className="text-white text-sm">
                        Venues:{" "}
                        {hotel.associatedVenues.map((v) => v.name).join(", ") ||
                          "None"}
                      </p>
                    </div>
                    <div className="flex flex-row gap-2 mt-2 md:mt-0">
                      <button
                        onClick={() => editHotel(hotel)}
                        className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded-md transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => togglePublishHotel(hotel._id, true)}
                        className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded-md transition-colors"
                      >
                        Publish
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HotelTab;
